import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@heroui/react';
import { RotateCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TOPICS, localizedSubtopicTitle, localizedTopicTitle } from '@/entities/topic';
import { ROUTES } from '@/shared/config';
import { readStorage, writeStorage } from '@/shared/lib/storage';
import { Sheet } from '@/shared/ui';
import { Reel, REEL_VIEWPORT_HEIGHT_PX, ReelSelectionWindow, type ReelItem } from './Reel';

const FIRST_SPIN_DELAY_MS = 500;

/**
 * Сколько последних выпавших тем не выпадает снова. Честный жребий из 50
 * всё равно повторяется: из пяти спинов подряд хотя бы одно совпадение — в ~19%
 * случаев, и человек видит это как «опять та же тема».
 */
const RECENT_TOPICS_LIMIT = 10;
const RECENT_TOPICS_KEY = 'topicRoulette.recentTopics';

function readRecentTopics(): string[] {
  return (
    readStorage(RECENT_TOPICS_KEY, (raw) =>
      Array.isArray(raw) ? raw.filter((id): id is string => typeof id === 'string') : null,
    ) ?? []
  );
}

function rememberTopic(id: string) {
  const recent = [id, ...readRecentTopics().filter((item) => item !== id)];
  writeStorage(RECENT_TOPICS_KEY, recent.slice(0, RECENT_TOPICS_LIMIT));
}

/** Барабан тем — это `TOPICS` один в один, так что индекс строки = индекс темы. */
function pickFreshTopicIndex(count: number): number {
  const recent = new Set(readRecentTopics());
  const fresh = TOPICS.flatMap((item, index) => (recent.has(item.id) ? [] : [index]));
  const pool = fresh.length > 0 ? fresh : TOPICS.map((_, index) => index);
  return pool[Math.floor(Math.random() * pool.length)] ?? Math.floor(Math.random() * count);
}

const SPIN_BUTTON_CLASS = [
  'flex size-7 shrink-0 items-center justify-center rounded-full',
  'border border-hairline bg-bg text-ink transition-colors active:bg-glass-3',
  'outline-none focus-visible:ring-2 focus-visible:ring-signal-blue/40',
  'disabled:pointer-events-none disabled:opacity-35',
].join(' ');

/**
 * Рулетка случайной темы в шторке, открывается с промо-карточки на главной.
 * Раньше была отдельной страницей (`/topic-picker`) — там барабаны терялись
 * в пустом пространстве экрана, поэтому переехали сюда.
 *
 * Два барабана side-by-side, как минуты и секунды в пикере времени iOS: левый
 * крутит широкую тему (`TOPICS`), правый — одну из её 5 подтем. Подтема и есть
 * финальный топик разговора (её лексика уходит в чат вместе с
 * `topicId`/`subtopicIndex`, см. `tutor-select` → `ChatPage`).
 *
 * Спин — последовательность: сперва тема, и только когда она остановилась,
 * стартует барабан подтемы по своим пяти строкам. Строка, которую барабан
 * проезжает прямо сейчас, оседает в рефе (`liveTopicIndexRef`), а не в
 * состоянии — иначе список подтем пересчитывался бы на каждой строке и
 * сбрасывал второй барабан посреди спина темы. Шторка монтируется только
 * открытой, поэтому каждое открытие — свежий спин с нуля. Первый спин — через
 * `FIRST_SPIN_DELAY_MS`, а не сразу: пусть шторка сперва выедет, и барабан
 * тронется уже на глазах.
 */
export function TopicRouletteSheet({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation('TopicRouletteSheet');
  const navigate = useNavigate();
  const lang = i18n.language;

  const [settledTopicIndex, setSettledTopicIndex] = useState(0);
  const [settledSubtopicIndex, setSettledSubtopicIndex] = useState(0);
  const liveTopicIndexRef = useRef(0);
  const liveSubtopicIndexRef = useRef(0);

  const [topicSpinTrigger, setTopicSpinTrigger] = useState(0);
  const [subtopicSpinTrigger, setSubtopicSpinTrigger] = useState(0);
  const [isTopicSpinning, setTopicSpinning] = useState(true);
  const [isSubtopicSpinning, setSubtopicSpinning] = useState(false);
  // Пока ждём первый спин, кнопки и «Начать» тоже закрыты — `isTopicSpinning` с `true`.
  const isSpinning = isTopicSpinning || isSubtopicSpinning;

  useEffect(() => {
    const timer = setTimeout(() => setTopicSpinTrigger(1), FIRST_SPIN_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const topic = TOPICS[settledTopicIndex] ?? TOPICS[0];

  // `useMemo` не по рефлексу: `Reel` сбрасывает ленту на смену идентичности
  // `items` — новый массив на каждый рендер обнулял бы барабан посреди спина.
  const topicItems = useMemo<ReelItem[]>(
    () => TOPICS.map((item) => ({ key: item.id, label: localizedTopicTitle(item, lang) })),
    [lang],
  );
  const subtopicItems = useMemo<ReelItem[]>(
    () =>
      topic.subtopics.map((item, index) => ({
        key: `${topic.id}-${index}`,
        label: localizedSubtopicTitle(item, lang),
      })),
    [topic, lang],
  );

  const handleTopicSpinningChange = (spinning: boolean) => {
    setTopicSpinning(spinning);
    if (!spinning) {
      setSettledTopicIndex(liveTopicIndexRef.current);
      const settled = TOPICS[liveTopicIndexRef.current];
      if (settled) rememberTopic(settled.id);
      setSubtopicSpinTrigger((n) => n + 1);
    }
  };

  const handleSubtopicSpinningChange = (spinning: boolean) => {
    setSubtopicSpinning(spinning);
    if (!spinning) setSettledSubtopicIndex(liveSubtopicIndexRef.current);
  };

  const start = () => {
    const query = new URLSearchParams({
      topicId: topic.id,
      subtopicIndex: String(settledSubtopicIndex),
    });
    void navigate(`${ROUTES.tutorSelect}?${query.toString()}`);
  };

  const ariaLabel = (item: ReelItem) => t('Reel:currentItemAria', { item: item.label });

  return (
    <Sheet
      title={t('title')}
      onClose={onClose}
      footer={
        <Button
          fullWidth
          className="h-14 bg-signal-blue text-base text-white"
          isDisabled={isSpinning}
          onPress={start}
        >
          {t('startButton')}
        </Button>
      }
    >
      <div className="flex flex-col gap-3 pt-2 pb-2">
        <div className="flex w-full justify-center gap-3 text-sm font-bold tracking-wide text-black uppercase">
          <div className="flex min-w-0 flex-1 items-center justify-center gap-1.5">
            <span className="truncate">{t('topicLabel')}</span>
            <button
              type="button"
              onClick={() => setTopicSpinTrigger((n) => n + 1)}
              disabled={isSpinning}
              aria-label={t('spinTopicAria')}
              className={SPIN_BUTTON_CLASS}
            >
              <RotateCw className="size-3.5 shrink-0" aria-hidden="true" />
            </button>
          </div>
          <div className="flex min-w-0 flex-1 items-center justify-center gap-1.5">
            <span className="truncate">{t('subtopicLabel')}</span>
            <button
              type="button"
              onClick={() => setSubtopicSpinTrigger((n) => n + 1)}
              disabled={isSpinning}
              aria-label={t('spinSubtopicAria')}
              className={SPIN_BUTTON_CLASS}
            >
              <RotateCw className="size-3.5 shrink-0" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div
          className="relative flex w-full justify-center gap-3"
          style={{ height: REEL_VIEWPORT_HEIGHT_PX }}
        >
          <ReelSelectionWindow />
          <Reel
            className="flex-1"
            items={topicItems}
            spinTrigger={topicSpinTrigger}
            onSelect={(index) => {
              liveTopicIndexRef.current = index;
            }}
            onSpinningChange={handleTopicSpinningChange}
            pickIndex={pickFreshTopicIndex}
            ariaLabel={ariaLabel}
          />
          <Reel
            className="flex-1"
            items={subtopicItems}
            spinTrigger={subtopicSpinTrigger}
            onSelect={(index) => {
              liveSubtopicIndexRef.current = index;
            }}
            onSpinningChange={handleSubtopicSpinningChange}
            ariaLabel={ariaLabel}
          />
        </div>
      </div>
    </Sheet>
  );
}
